import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Cross from '@oberoninternal/travelbase-ds/components/figure/Cross';
import Plus from '@oberoninternal/travelbase-ds/components/figure/Plus';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import { UnreachableCaseError } from '@oberoninternal/travelbase-ds/entities/UnreachableCaseError';
import Package from '@oberoninternal/travelbase-ds/svg/Package.svg';
import Special from '@oberoninternal/travelbase-ds/svg/Special.svg';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import React, { FC, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Booking } from '../entities/Booking';
import { PlannerTripFragment, SpecialQuery, TripTypeEnum, useSpecialQuery } from '../generated/graphql';
import { StyledRoundButton } from '@oberoninternal/travelbase-ds/components/action/RoundButton';
import Stack from './Stack';

export const query = gql`
    query Special($id: ID!) {
        special(id: $id) {
            id
            description
            landingPageUrl
            name
        }
    }
`;

interface Props {
    trip: PlannerTripFragment;
    onRemoveSpecialTrip: () => void;
    onAddSpecialTrip: (trip: PlannerTripFragment) => void;
    booking: Booking;
}

export const renderSpecial = ({
    type,
    data,
    loading,
    price,
    originalPrice,
    trip,
    onAddSpecialTrip,
    onRemoveSpecialTrip: onRemoveSpecial,
    hasSelectedSpecial,
}: {
    type: TripTypeEnum;
    data: Maybe<SpecialQuery>;
    loading: boolean;
    booking: Booking;
    price: number;
    originalPrice: Maybe<number>;
    trip: PlannerTripFragment;
    onAddSpecialTrip: (trip: PlannerTripFragment) => void;
    onRemoveSpecialTrip?: () => void;
    hasSelectedSpecial: boolean;
}) => {
    switch (type) {
        case TripTypeEnum.Special: {
            if (loading) {
                return <Skeleton />;
            }

            if (!data?.special?.id) {
                return null;
            }

            const {
                special: { description, name, landingPageUrl },
            } = data;

            return (
                <InnerSpecial
                    icon={originalPrice ? <Special /> : <Package />}
                    description={description}
                    title={name}
                    price={price}
                    originalPrice={originalPrice}
                    button={
                        <>
                            <Button
                                variant={hasSelectedSpecial ? 'outline' : 'primary'}
                                onClick={hasSelectedSpecial ? onRemoveSpecial : () => onAddSpecialTrip(trip)}
                                className="gt-m"
                            >
                                {hasSelectedSpecial ? (
                                    <>
                                        <Cross style={{ marginRight: '0.8rem' }} />
                                        <FormattedMessage defaultMessage="verwijderen" />
                                    </>
                                ) : (
                                    <>
                                        <Plus style={{ marginRight: '0.8rem' }} />
                                        <FormattedMessage defaultMessage="kiezen" />
                                    </>
                                )}
                            </Button>
                            <StyledRoundButton
                                variant={hasSelectedSpecial ? 'outline' : 'primary'}
                                onClick={hasSelectedSpecial ? onRemoveSpecial : () => onAddSpecialTrip(trip)}
                                className="lt-m"
                            >
                                {hasSelectedSpecial ? (
                                    <>
                                        <Cross />
                                    </>
                                ) : (
                                    <div>
                                        <Plus />
                                    </div>
                                )}
                            </StyledRoundButton>
                        </>
                    }
                    landingPageUrl={landingPageUrl}
                />
            );
        }
        case TripTypeEnum.LastMinute: {
            return (
                <InnerSpecial
                    icon={<Special />}
                    title={<FormattedMessage defaultMessage="Last-minute korting" />}
                    description={
                        <FormattedMessage defaultMessage="Op het laatste moment een vakantie of weekendje weg boeken? Texel heeft een uitgebreid aanbod last minute aanbiedingen, waarmee u profiteert van aantrekkelijke kortingen!" />
                    }
                    originalPrice={originalPrice}
                    price={price}
                />
            );
        }
        case TripTypeEnum.EarlyBooking: {
            return (
                <InnerSpecial
                    icon={<Special />}
                    title={<FormattedMessage defaultMessage="Vroegboekkorting" />}
                    description={<FormattedMessage defaultMessage="Hier komt de vroegboektext te staan" />}
                    originalPrice={originalPrice}
                    price={price}
                />
            );
        }

        default:
            throw new UnreachableCaseError(type);
    }
};

const SpecialTrip: FC<React.PropsWithChildren<Props>> = ({ trip, booking, onAddSpecialTrip, onRemoveSpecialTrip }) => {
    const { specialId, type, price, originalPrice } = trip;
    if (type === TripTypeEnum.Regular) {
        throw new Error(`This trip is a regular trip. Make sure only special trips get passed to this component`);
    }
    // query will be skipped if there's no specialId, hence the assertion
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { data, loading } = useSpecialQuery({ variables: { id: specialId! }, skip: !specialId });

    return renderSpecial({
        type,
        data,
        loading,
        booking,
        price,
        originalPrice,
        trip,
        onAddSpecialTrip,
        onRemoveSpecialTrip,
        hasSelectedSpecial: booking.trip?.specialId === specialId,
    });
};

interface InnerProps {
    title: ReactNode;
    description: ReactNode;
    price: Maybe<number>;
    originalPrice: Maybe<number>;
    landingPageUrl?: Maybe<string>;
    button?: ReactNode;
    icon: ReactNode;
}

const InnerSpecial = ({ title, description, price, landingPageUrl, button, icon, originalPrice }: InnerProps) => (
    <Container>
        <Box pr={4} flex={1}>
            <TitleWrapper>
                {icon}
                <Title>{title}</Title>
            </TitleWrapper>
            <StyledBody>
                {description}{' '}
                {landingPageUrl && (
                    <a href={landingPageUrl} target="_blank" rel="noreferrer noopener">
                        <FormattedMessage defaultMessage="Lees meer" />
                    </a>
                )}
            </StyledBody>
        </Box>
        {price || originalPrice ? (
            <Flex alignItems="center" justifyContent="space-between">
                <Flex alignItems="flex-end" mr={4}>
                    <Stack variant="inline" spacing={3} alignItems="center">
                        {originalPrice && (
                            <Body variant="small">
                                <FormattedMessage
                                    defaultMessage="van {price}"
                                    values={{
                                        price: (
                                            <OriginalPrice>
                                                <FormattedNumber format="EUR" value={originalPrice} />
                                            </OriginalPrice>
                                        ),
                                    }}
                                />
                            </Body>
                        )}
                        <Body variant="large">
                            {originalPrice && (
                                <>
                                    <FormattedMessage defaultMessage="voor" />{' '}
                                </>
                            )}
                            {price && (
                                <Price>
                                    <FormattedNumber format="EUR" value={price} />
                                </Price>
                            )}
                        </Body>
                    </Stack>
                </Flex>
            </Flex>
        ) : null}
        {button}
    </Container>
);

export default SpecialTrip;

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.primary['80']};
`;

const Price = styled.span`
    ${({ theme }) => getFontCss(theme.fontSizes.body.huge)};
    font-weight: 600;
`;

const OriginalPrice = styled.span`
    ${({ theme }) => getFontCss(theme.fontSizes.body.regular)};
    text-decoration: line-through;
    color: ${({ theme }) => theme.colors.neutral['50']};
`;

const Title = styled(Body)`
    font-weight: bolder;
    margin-left: ${({ theme }) => theme.spacing['30_Small']};
`;

const StyledBody = styled(Body)`
    margin-top: 0.6rem;
    max-width: 68.8rem;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing['50_Semi']};
    width: 100%;
    border: ${({ theme }) => theme.colors.neutral['20']} 1px solid;
    border-radius: 0.8rem;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;
